import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from './cn'

export type EmptyStateProps = {
  icon?: LucideIcon
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  /** Optional secondary action (e.g. "Clear filters" beside a primary). */
  secondaryAction?: ReactNode
  /**
   * - `panel` (default) — list/table empty. Stretches to fill the available
   *   vertical space in flex-column parents and vertically centers its
   *   contents, so it never floats at the top of a tall empty page.
   * - `inline` — inside a fixed-height card (analytics chart, comms inbox,
   *   product grid). Compact, does not stretch.
   * - `page` — first-run full-surface hero. Large tile + dashed border +
   *   panel background. The action slot IS the page's primary CTA.
   */
  variant?: 'panel' | 'inline' | 'page'
}

/**
 * Empty-state placeholder. Three parts per NNG + Polaris: status + context
 * + action. Never blank.
 *
 * Why the default stretches: every list-page in the app uses a flex-column
 * `.page` or `.list-page` root. When a query returns zero rows, the empty
 * state used to hug the top and leave a huge dead zone below. The default
 * variant now grows to fill the remaining column height and centers its
 * content — matches Polaris / NNG guidance that empty states belong
 * centered in the surface they replaced.
 *
 * Use `inline` when the empty state lives inside a short card that should
 * stay at its natural height.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'panel',
}: EmptyStateProps) {
  const isPage = variant === 'page'
  const isInline = variant === 'inline'
  const IconComp = icon
  return (
    <div
      className={cn(
        'empty-state',
        isPage && 'empty-state--page',
        isInline && 'empty-state--inline',
      )}
      role="status"
    >
      {IconComp && (
        <div className="empty-state-icon" aria-hidden="true">
          <IconComp size={isPage ? 26 : 20} />
        </div>
      )}
      <div className="empty-state-title">{title}</div>
      {description && <div className="empty-state-desc">{description}</div>}
      {(action || secondaryAction) && (
        <div className="empty-state-actions">
          {secondaryAction}
          {action}
        </div>
      )}
    </div>
  )
}
